import Column from '@amzn/meridian/column';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ContactDetails,
  ConsolidatedContact,
  defaultContactDetails,
  ContactViewTabObject,
} from '../../../../api/CoachingNotes/schema';
import useCustomTracking from '../../../../Util/useCustomTracking';
import ModalForm from '../../Modal/ContactModal/Form';
import { RecentTabFilter } from './Filter/RecentTabFilter';
import { isFeatureEnabled, Feature } from '../../../../config/FeaturesConfig';

import {
  PAGINATION_SIZE,
  customSort,
  EmptyFilterView,
  formatDuration,
  getChannelI18nKey,
  getOwnershipI18nKey,
  mapToActionStatus,
  NoContactView,
  NOT_FOUND_LABEL,
  SortableColumn,
  SortableDirection,
  StatusText,
  TabProps,
} from './TabHelper';

export const RecentTab: React.FC<TabProps> = ({
  contacts,
  username,
  userID,
  sourcePage,
}: TabProps): React.ReactElement => {
  const intl = useIntl();
  const [sortColumn, setSortColumn] = useState(SortableColumn.INIT);
  const [sortDirection, setSortDirection] = useState(
    SortableDirection.DECENDING
  );
  const [sortedContacts, setSortedContacts] = useState(
    contacts.sort((noteA: ConsolidatedContact, noteB: ConsolidatedContact) =>
      customSort(sortColumn, sortDirection, noteA, noteB)
    )
  );
  const [filteredContacts, setFilteredContacts] = useState(sortedContacts);

  // eslint-disable-next-line no-shadow
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: SortableColumn;
      sortDirection: SortableDirection;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
      setSortedContacts(
        contacts.sort((a, b) => customSort(sortColumn, sortDirection, a, b))
      );
    },
    []
  );

  const SACoaching = isFeatureEnabled(Feature.SA_COACHING);
  const [curPage, setCurPage] = useState(1); // 1-based index
  const [pageCount, setPageCount] = useState(
    Math.max(1, Math.ceil(filteredContacts.length / PAGINATION_SIZE))
  );
  const [resultsCount, setResultsCount] = useState('');

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'coaching-notes-recent-table-sort',
    'coaching-notes-recent-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
      'data-csa-c-tag1': sourcePage || '',
      'data-csa-c-tag2': 'Contact Table',
      'data-csa-c-tag3': 'RECENT',
    }
  );

  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setPageCount(
      Math.max(1, Math.ceil(filteredContacts.length / PAGINATION_SIZE))
    );
    if (filteredContacts.length > 1) {
      setResultsCount(
        filteredContacts.length +
          ' ' +
          intl.formatMessage({
            id: 'cs_perfportal_coaching_note_results',
            defaultMessage: 'results',
          })
      );
    } else {
      setResultsCount(
        filteredContacts.length +
          ' ' +
          intl.formatMessage({
            id: 'cs_perfportal_coaching_note_result',
            defaultMessage: 'result',
          })
      );
    }
  }, [filteredContacts]);

  if (sortedContacts.length === 0) {
    return <NoContactView />;
  }

  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <Row width='100%' alignmentHorizontal='start'>
        <Text>
          <FormattedMessage
            id='cs_perfportal_consolidated_recent_subtitle'
            defaultMessage="Search the associate's contacts from the last 4 weeks."
          />
        </Text>
      </Row>
      <Table
        headerRows={1}
        showStripes
        showDividers
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === SortableDirection.ASCENDING
            ? 'ascending'
            : 'descending'
        }
        onSort={onSort}
        data-cy={'recent-table'}
      >
        <TableSortTrackingTag />
        <TableActionBar>
          <RecentTabFilter
            contacts={sortedContacts}
            setFilteredContacts={setFilteredContacts}
            setCurPage={setCurPage}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            sourcePage={sourcePage}
          />
        </TableActionBar>

        {filteredContacts.length === 0 && <EmptyFilterView />}
        {filteredContacts.length !== 0 && (
          <TableRow highlightOnHover={false}>
            <TableCell width='13%' sortColumn={SortableColumn.CONTACT_ID}>
              <FormattedMessage
                id='PerfPortal_contactId'
                defaultMessage='Contact ID'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.CONTACT_DATE}>
              <FormattedMessage
                id='cs_perfportal_contact_recom_contact_date'
                defaultMessage='Contact date'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.SIC}>
              <FormattedMessage
                id='cs_perfportal_contact_recom_sic'
                defaultMessage='SIC'
                description='SIC code for recommended contact'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.DURATION}>
              <FormattedMessage
                id='cs_perfportal_duration'
                defaultMessage='Duration'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.CHANNEL}>
              <FormattedMessage
                id='cs_perfportal_channel'
                defaultMessage='Channel'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.OWNERSHIP}>
              <FormattedMessage
                id='cs_perfportal_ownership'
                defaultMessage='Ownership'
              />
            </TableCell>
            <TableCell width='13%' sortColumn={SortableColumn.STATUS}>
              <FormattedMessage
                id='cs_perfportal_status'
                defaultMessage='Status'
                description='Status for Contacts'
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id='cs_perfportal_actions'
                defaultMessage='Actions'
              />
            </TableCell>
          </TableRow>
        )}

        {filteredContacts.length !== 0 &&
          filteredContacts
            .slice((curPage - 1) * PAGINATION_SIZE, curPage * PAGINATION_SIZE)
            .map((contact: ConsolidatedContact, index) => {
              const actionStatus = mapToActionStatus(contact);
              const contactInfo: ContactDetails = {
                ...defaultContactDetails,
                ...(contact.contactInfo || {}),
                cscContactID: contact.contactID,
              };

              return (
                <TableRow
                  highlightOnHover={false}
                  key={contact.contactID + index}
                  data-cy={'recent-contact-row'}
                >
                  <TableCell width='13%'>
                    <Row>
                      <Text>{contact.contactID}</Text>
                    </Row>
                    <Row>
                      <Text>{contact.skill ?? ''}</Text>
                    </Row>
                  </TableCell>
                  <TableCell width='13%'>
                    {moment(contact.contactDate).format('YYYY-MM-DD')}
                  </TableCell>
                  <TableCell width='13%'>
                    <Text>{contact.sic ?? ''}</Text>
                  </TableCell>
                  <TableCell width='13%'>
                    <Text>{formatDuration(contact.duration ?? '')}</Text>
                  </TableCell>
                  <TableCell width='13%'>
                    <Text>
                      <FormattedMessage
                        id={getChannelI18nKey(contact.channel ?? '')}
                        defaultMessage={contact.channel ?? NOT_FOUND_LABEL}
                      />
                    </Text>
                  </TableCell>
                  <TableCell width='13%'>
                    <Text>
                      <FormattedMessage
                        id={getOwnershipI18nKey(contact.ownership ?? '')}
                        defaultMessage={contact.ownership ?? NOT_FOUND_LABEL}
                      />
                    </Text>
                  </TableCell>
                  <TableCell width='13%'>
                    <StatusText
                      data-cy={`contact-status-${contact.contactID}`}
                      status={actionStatus.status}
                      date={actionStatus.date}
                    />
                  </TableCell>
                  <TableCell>
                    <ModalForm
                      parentValues={contact}
                      initialValues={contactInfo}
                      action={actionStatus.action}
                      parentUserName={username}
                      parentUserID={userID}
                      contactTab={ContactViewTabObject.RECENT}
                      sourcePage={sourcePage}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
      </Table>
      {SACoaching ? (
        <Row
          width='50%'
          widths={['grid-4', 'grid-8']}
          alignmentVertical='center'
          spacing='300'
        >
          <Box>
            <Text alignment='right'>{resultsCount}</Text>
          </Box>
          <Box>
            <Pagination
              numberOfPages={pageCount}
              onChange={setCurPage}
              currentPage={curPage}
            />
          </Box>
        </Row>
      ) : (
        <Pagination
          numberOfPages={pageCount}
          onChange={setCurPage}
          currentPage={curPage}
        />
      )}
    </Column>
  );
};
